import Button from 'Components/Atoms/Button/Button';
import OutlineAdd from 'Components/Atoms/OutlineIcons/OutlineAdd';
import useDrawerStack from 'Hooks/useDrawerStack';
import { useTranslation } from 'react-i18next';
import { DRAWER_STACK, PAYMENT_METHODS_TYPES } from 'Utils/constants';
import { createInvoicePaymentMethod, deleteInvoicePaymentMethod, updateInvoicePaymentMethod } from 'Services/invoices';
import Loading from 'Components/Atoms/Loading/Loading';
import { useQueryClient } from '@tanstack/react-query';
import IconButton from 'Components/Atoms/IconButton/IconButton';
import Icon from 'Components/Atoms/Icon/Icon';
import { useState } from 'react';
import PropTypes from 'prop-types';
import { cn } from 'Utils/cn';
import {
    useFloating,
    useClick,
    useDismiss,
    useRole,
    useInteractions,
    FloatingFocusManager,
    offset,
    flip,
    autoUpdate,
} from '@floating-ui/react';
import { AnimatePresence, motion } from 'framer-motion';
import useInvoicePaymentMethods from 'Hooks/useInvoicePaymentMethods';
import useProvidersConnections from 'Hooks/useProvidersConnections';
import Tooltip from 'Components/Molecules/Tooltip/Tooltip';

function InvoicePaymentMethodRow({ paymentMethod, onEdit, onDelete, className }) {
    const { name, type, is_default } = paymentMethod;
    const [open, setOpen] = useState(false);

    const { t } = useTranslation('invoices');

    const PAYMENT_METHODS_LABELS = {
        [PAYMENT_METHODS_TYPES.CASH]: t('cash'),
        [PAYMENT_METHODS_TYPES.BANK_DIRECT_DEBIT]: t('direct-debit'),
        [PAYMENT_METHODS_TYPES.BANK_TRANSFER]: t('bank-transfer'),
        [PAYMENT_METHODS_TYPES.CREDIT_CARD]: t('credit-card'),
        [PAYMENT_METHODS_TYPES.FACTORING]: t('factoring'),
        [PAYMENT_METHODS_TYPES.PAYPAL]: t('paypal'),
        [PAYMENT_METHODS_TYPES.BIZUM]: t('bizum'),
        [PAYMENT_METHODS_TYPES.CHECK]: t('check'),
        [PAYMENT_METHODS_TYPES.OTHER]: t('other'),
    };

    const paymentTypeLabel = PAYMENT_METHODS_LABELS[type] || '-';

    const { refs, floatingStyles, context, strategy, x, y } = useFloating({
        open,
        onOpenChange: setOpen,
        whileElementsMounted: autoUpdate,
        placement: 'bottom-end',
        middleware: [
            flip(),
            offset({
                mainAxis: -8,
                crossAxis: -4,
            }),
        ],
    });

    const { getFloatingProps, getReferenceProps } = useInteractions([
        useClick(context),
        useRole(context, { role: 'listbox' }),
        useDismiss(context),
    ]);

    return (
        <div className={cn('grid grid-cols-[2fr_1fr_auto] gap-2 items-center py-0.5', className)}>
            <button
                type="button"
                onClick={() => onEdit(paymentMethod)}
                className="pl-3 text-sm flex items-center gap-2 grow min-w-0 text-gray-900"
            >
                <Icon name="credit-card" color="currentColor" className="shrink-0" size={20} />
                <div className="font-semibold min-w-0 truncate">{name}</div>
            </button>
            <div className="text-xs truncate text-gray-500">{paymentTypeLabel}</div>
            <div className="relative flex items-center">
                <div className="flex justify-center items-center w-[20px] -mr-1">
                    {is_default && (
                        <Tooltip label={t('default-payment-method')}>
                            <span className="text-dark-blue opacity-40 inline-flex">
                                <Icon name="circle-check" size={20} color="currentColor" />
                            </span>
                        </Tooltip>
                    )}
                </div>

                <IconButton
                    ref={refs.setReference}
                    {...getReferenceProps()}
                    icon={<Icon name="dots-vertical" />}
                    variant="ghost"
                    onClick={() => setOpen(!open)}
                    className={cn(open && 'text-dark-blue')}
                />

                <AnimatePresence>
                    {open && (
                        <FloatingFocusManager context={context}>
                            <motion.div
                                ref={refs.setFloating}
                                style={{
                                    ...floatingStyles,
                                    position: strategy,
                                    top: y ?? 0,
                                    left: x ?? 0,
                                }}
                                className="rounded-md bg-white ring ring-black/5 shadow-sm z-dropdown min-w-[100px]"
                                {...getFloatingProps()}
                                initial={{ opacity: 0, y: -8 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -8 }}
                                transition={{ duration: 0.15, ease: 'easeOut' }}
                            >
                                <div className="p-1 grid">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            onEdit(paymentMethod);
                                            setOpen(false);
                                        }}
                                        className="text-sm px-2 py-1.5 rounded-sm transition-colors text-left font-medium hover:bg-gray-100"
                                    >
                                        {t('edit')}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            onDelete(paymentMethod);
                                            setOpen(false);
                                        }}
                                        disabled={!paymentMethod.is_deletable}
                                        className="text-sm px-2 py-1.5 rounded-sm transition-colors text-left font-medium hover:bg-gray-100 text-danger disabled:text-gray-400 disabled:hover:bg-transparent"
                                    >
                                        {t('delete')}
                                    </button>
                                </div>
                            </motion.div>
                        </FloatingFocusManager>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}

InvoicePaymentMethodRow.propTypes = {
    paymentMethod: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        type: PropTypes.oneOf(Object.values(PAYMENT_METHODS_TYPES)),
        is_deletable: PropTypes.bool.isRequired,
        is_default: PropTypes.bool.isRequired,
    }).isRequired,
    onEdit: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    className: PropTypes.string,
};

export default function InvoicesInvoicingPaymentMethodsDrawer() {
    const { paymentMethods, isLoading } = useInvoicePaymentMethods();
    const { open } = useDrawerStack();
    const { t } = useTranslation('invoices');

    const queryClient = useQueryClient();
    const { visibleProducts } = useProvidersConnections();

    const handleAddPaymentMethod = () => {
        const productId = visibleProducts.length > 0 ? visibleProducts[0].id : null;

        const hasDefault = paymentMethods.some((pm) => pm.is_default);

        const EMPTY_PAYMENT_METHOD = {
            name: '',
            type: PAYMENT_METHODS_TYPES.BANK_TRANSFER,
            description: '',
            is_default: !hasDefault,
            product_id: productId,
        };

        open(DRAWER_STACK.INVOICE_PAYMENT_METHODS_EDITOR, {
            title: t('new-payment-method'),
            paymentMethod: EMPTY_PAYMENT_METHOD,
            onSubmit: async (data) => {
                await createInvoicePaymentMethod(data);
                await queryClient.invalidateQueries(['invoice-payment-methods']);
            },
        });
    };

    const handleEditPaymentMethod = (paymentMethod) => {
        open(DRAWER_STACK.INVOICE_PAYMENT_METHODS_EDITOR, {
            title: t('edit-payment-method'),
            paymentMethod,
            onSubmit: async (data) => {
                await updateInvoicePaymentMethod(paymentMethod.id, data);
                await queryClient.invalidateQueries(['invoice-payment-methods']);
            },
        });
    };

    const handleDeletePaymentMethod = (paymentMethod) => {
        open(DRAWER_STACK.CONFIRMATION_DIALOG, {
            title: t('delete-payment-method'),
            description: t('delete-payment-method-description', { name: paymentMethod.name }),
            onSubmit: async () => {
                await deleteInvoicePaymentMethod(paymentMethod.id);
                await queryClient.invalidateQueries(['invoice-payment-methods']);
            },
        });
    };

    return (
        <div className="py-4">
            <div className="rounded-lg border border-gray-200 bg-white">
                {isLoading && (
                    <div className="py-4 flex justify-center">
                        <Loading />
                    </div>
                )}

                {!isLoading && paymentMethods.length === 0 && (
                    <div className="py-9 px-2 text-center text-sm text-gray-500 border-b border-gray-200">
                        {t('no-payment-methods')}
                    </div>
                )}

                {!isLoading && (
                    <div className="grid">
                        {paymentMethods.map((paymentMethod) => (
                            <InvoicePaymentMethodRow
                                className="border-b border-gray-200"
                                key={paymentMethod.id}
                                paymentMethod={paymentMethod}
                                onEdit={() => handleEditPaymentMethod(paymentMethod)}
                                onDelete={() => handleDeletePaymentMethod(paymentMethod)}
                            />
                        ))}
                    </div>
                )}

                <div className="py-0.5">
                    <Button variant="ghost" className="text-sm px-3" onClick={handleAddPaymentMethod}>
                        <OutlineAdd />
                        {t('add-payment-method')}
                    </Button>
                </div>
            </div>
        </div>
    );
}
